import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { filingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  FileCheck,
  Plus,
  ExternalLink,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  X,
  ChevronDown,
  RefreshCw,
  Trash2,
  FileText,
  CircleDot,
} from 'lucide-react';

const STATUS_CONFIG = {
  ready_to_file: { label: 'Ready to File', color: 'bg-slate-500', textColor: 'text-slate-600', icon: FileText },
  filed: { label: 'Filed', color: 'bg-blue-500', textColor: 'text-blue-600', icon: ArrowRight },
  receipt_received: { label: 'Receipt Received', color: 'bg-indigo-500', textColor: 'text-indigo-600', icon: FileCheck },
  biometrics_scheduled: { label: 'Biometrics Scheduled', color: 'bg-amber-500', textColor: 'text-amber-600', icon: Clock },
  interview_scheduled: { label: 'Interview Scheduled', color: 'bg-purple-500', textColor: 'text-purple-600', icon: CircleDot },
  decision_received: { label: 'Decision Received', color: 'bg-emerald-500', textColor: 'text-emerald-600', icon: CheckCircle2 },
};

const STATUSES = Object.keys(STATUS_CONFIG);

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ready_to_file;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <cfg.icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function StatusTimeline({ history }) {
  if (!history?.length) return null;
  return (
    <div className="space-y-3 mt-4" data-testid="status-timeline">
      {history.map((entry, idx) => {
        const cfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.ready_to_file;
        return (
          <div key={idx} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${cfg.color} shrink-0 mt-1`} />
              {idx < history.length - 1 && <div className="w-0.5 flex-1 bg-border mt-1" />}
            </div>
            <div className="pb-3">
              <p className="text-sm font-medium text-foreground">{cfg.label}</p>
              <p className="text-xs text-muted-foreground">{new Date(entry.date).toLocaleString()}</p>
              {entry.notes && <p className="text-xs text-muted-foreground mt-0.5">{entry.notes}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatsCard({ label, value, icon: Icon, sub, color = 'text-foreground' }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function OnlineFilingPage() {
  const [filings, setFilings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedFiling, setSelectedFiling] = useState(null);
  const [cases, setCases] = useState([]);
  const [forms, setForms] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [filingsRes, statsRes] = await Promise.all([
        filingAPI.list(statusFilter || undefined),
        filingAPI.stats(),
      ]);
      setFilings(filingsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('eFiling is not enabled for your organization');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-6 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-6 overflow-auto" data-testid="efiling-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">eFiling</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage USCIS online form filings and track status</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="new-filing-btn">
            <Plus className="w-4 h-4" />
            New Filing
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="filing-stats">
            <StatsCard label="Total Filings" value={stats.total} icon={FileCheck} color="text-primary" />
            <StatsCard label="Ready to File" value={stats.by_status?.ready_to_file || 0} icon={FileText} color="text-slate-600" />
            <StatsCard label="In Progress" value={(stats.by_status?.filed || 0) + (stats.by_status?.receipt_received || 0) + (stats.by_status?.biometrics_scheduled || 0) + (stats.by_status?.interview_scheduled || 0)} icon={Clock} color="text-blue-600" />
            <StatsCard label="Filing Fees" value={`$${(stats.total_fees || 0).toLocaleString()}`} icon={DollarSign} sub={`$${(stats.fees_paid || 0).toLocaleString()} paid / $${(stats.fees_pending || 0).toLocaleString()} pending`} color="text-emerald-600" />
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs font-medium text-muted-foreground">Filter:</span>
          <button
            onClick={() => setStatusFilter('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!statusFilter ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
            data-testid="filter-all"
          >
            All
          </button>
          {STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
              data-testid={`filter-${s}`}
            >
              {STATUS_CONFIG[s].label}
            </button>
          ))}
        </div>

        {/* Filings Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="filings-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left p-3 font-medium text-muted-foreground">Case / Client</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Form</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Filing Fee</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Receipt #</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Filed</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filings.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-muted-foreground">
                      <FileCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No filings found</p>
                      <p className="text-xs mt-1">Click "New Filing" to get started</p>
                    </td>
                  </tr>
                ) : filings.map(f => (
                  <tr key={f.id} className="border-b border-border hover:bg-secondary/30 cursor-pointer transition-colors" onClick={() => setSelectedFiling(f)} data-testid={`filing-row-${f.id}`}>
                    <td className="p-3 font-medium text-foreground">{f.case_name}</td>
                    <td className="p-3">
                      <span className="font-mono text-xs bg-secondary px-2 py-1 rounded">{f.form_type}</span>
                      <span className="text-xs text-muted-foreground ml-2 hidden lg:inline">{f.form_title}</span>
                    </td>
                    <td className="p-3"><StatusBadge status={f.status} /></td>
                    <td className="p-3">
                      <span className={f.fee_paid ? 'text-emerald-600' : 'text-amber-600'}>
                        ${(f.filing_fee || 0).toLocaleString()}
                      </span>
                      {f.fee_paid && <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" />}
                    </td>
                    <td className="p-3 font-mono text-xs">{f.receipt_number || '—'}</td>
                    <td className="p-3 text-xs text-muted-foreground">{f.filing_date ? new Date(f.filing_date).toLocaleDateString() : '—'}</td>
                    <td className="p-3 text-right">
                      <a href={f.uscis_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`uscis-link-${f.id}`}>
                        USCIS <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Filing Modal */}
        {showCreate && (
          <CreateFilingModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); fetchData(); }}
            cases={cases}
            setCases={setCases}
            forms={forms}
            setForms={setForms}
          />
        )}

        {/* Filing Detail Modal */}
        {selectedFiling && (
          <FilingDetailModal
            filing={selectedFiling}
            onClose={() => setSelectedFiling(null)}
            onUpdated={() => { setSelectedFiling(null); fetchData(); }}
          />
        )}
      </main>
    </div>
  );
}

function CreateFilingModal({ onClose, onCreated, cases, setCases, forms, setForms }) {
  const [caseId, setCaseId] = useState('');
  const [formType, setFormType] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [casesRes, formsRes] = await Promise.all([
          import('../lib/api').then(m => m.default.get('/cases')),
          filingAPI.getForms(),
        ]);
        setCases(casesRes.data);
        setForms(formsRes.data);
      } catch { /* silent */ }
      setLoadingData(false);
    };
    if (!cases.length || !forms.length) load();
    else setLoadingData(false);
  }, [cases.length, forms.length, setCases, setForms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!caseId || !formType) return;
    setCreating(true);
    try {
      await filingAPI.create({ case_id: caseId, form_type: formType, notes });
      toast.success('Filing created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create filing');
    } finally {
      setCreating(false);
    }
  };

  const selectedForm = forms.find(f => f.name === formType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-filing-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-foreground">New USCIS Filing</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary"><X className="w-5 h-5" /></button>
        </div>

        {loadingData ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Case / Client *</label>
              <select
                value={caseId}
                onChange={e => setCaseId(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                required
                data-testid="filing-case-select"
              >
                <option value="">Select a case...</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>{c.name} {c.a_number ? `(${c.a_number})` : ''}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Form Type *</label>
              <select
                value={formType}
                onChange={e => setFormType(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                required
                data-testid="filing-form-select"
              >
                <option value="">Select a form...</option>
                {forms.map(f => (
                  <option key={f.name} value={f.name}>{f.name} - {f.title}</option>
                ))}
              </select>
            </div>

            {selectedForm && (
              <div className="bg-secondary/50 border border-border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{selectedForm.name} - {selectedForm.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">USCIS Filing Fee: <strong className="text-foreground">${selectedForm.filing_fee}</strong></p>
                  </div>
                  <a href={selectedForm.uscis_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1 hover:underline">
                    USCIS Portal <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground resize-none"
                rows={3}
                placeholder="Optional notes about this filing..."
                data-testid="filing-notes"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={creating || !caseId || !formType} className="flex-1 gap-2" data-testid="create-filing-submit">
                {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Filing
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function FilingDetailModal({ filing, onClose, onUpdated }) {
  const [status, setStatus] = useState(filing.status);
  const [filingDate, setFilingDate] = useState(filing.filing_date || '');
  const [confirmationNumber, setConfirmationNumber] = useState(filing.confirmation_number || '');
  const [receiptNumber, setReceiptNumber] = useState(filing.receipt_number || '');
  const [priorityDate, setPriorityDate] = useState(filing.priority_date || '');
  const [biometricsDate, setBiometricsDate] = useState(filing.biometrics_date || '');
  const [interviewDate, setInterviewDate] = useState(filing.interview_date || '');
  const [feePaid, setFeePaid] = useState(filing.fee_paid || false);
  const [feePaymentDate, setFeePaymentDate] = useState(filing.fee_payment_date || '');
  const [notes, setNotes] = useState(filing.notes || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await filingAPI.update(filing.id, {
        status,
        filing_date: filingDate || null,
        confirmation_number: confirmationNumber || null,
        receipt_number: receiptNumber || null,
        priority_date: priorityDate || null,
        biometrics_date: biometricsDate || null,
        interview_date: interviewDate || null,
        fee_paid: feePaid,
        fee_payment_date: feePaymentDate || null,
        notes: notes || null,
      });
      toast.success('Filing updated');
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this filing? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await filingAPI.delete(filing.id);
      toast.success('Filing deleted');
      onUpdated();
    } catch {
      toast.error('Failed to delete filing');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="filing-detail-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{filing.case_name}</h2>
            <p className="text-sm text-muted-foreground">{filing.form_type} - {filing.form_title}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={filing.uscis_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90" data-testid="uscis-portal-btn">
              Open USCIS Portal <ExternalLink className="w-3 h-3" />
            </a>
            <button onClick={onClose} className="p-1 rounded hover:bg-secondary"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: Form fields */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm"
                data-testid="filing-status-select"
              >
                {STATUSES.map(s => (
                  <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Filing Date</label>
              <input type="date" value={filingDate} onChange={e => setFilingDate(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" data-testid="filing-date-input" />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Confirmation Number</label>
              <input type="text" value={confirmationNumber} onChange={e => setConfirmationNumber(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" placeholder="e.g., IOE-0123456789" data-testid="filing-confirmation-input" />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Receipt Number</label>
              <input type="text" value={receiptNumber} onChange={e => setReceiptNumber(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" placeholder="e.g., MSC-21-900-12345" data-testid="filing-receipt-input" />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Priority Date</label>
              <input type="date" value={priorityDate} onChange={e => setPriorityDate(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" data-testid="filing-priority-input" />
            </div>

            <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg space-y-2">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Deadline Dates (Alerts Enabled)
              </p>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Biometrics Date</label>
                <input type="date" value={biometricsDate} onChange={e => setBiometricsDate(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" data-testid="filing-biometrics-date-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Interview Date</label>
                <input type="date" value={interviewDate} onChange={e => setInterviewDate(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" data-testid="filing-interview-date-input" />
              </div>
              <p className="text-[10px] text-muted-foreground/60">Alerts sent 7, 3, and 1 day before these dates</p>
            </div>

            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
              <div className="flex-1">
                <p className="text-sm font-medium">Filing Fee: ${(filing.filing_fee || 0).toLocaleString()}</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={feePaid} onChange={e => setFeePaid(e.target.checked)} className="rounded" data-testid="filing-fee-paid-checkbox" />
                <span className="text-sm">Paid</span>
              </label>
            </div>

            {feePaid && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Payment Date</label>
                <input type="date" value={feePaymentDate} onChange={e => setFeePaymentDate(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm" data-testid="filing-payment-date-input" />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm resize-none" rows={3} data-testid="filing-detail-notes" />
            </div>
          </div>

          {/* Right: Status Timeline */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Filing Progress</h3>

            {/* Progress tracker */}
            <div className="space-y-1 mb-4">
              {STATUSES.map((s, idx) => {
                const cfg = STATUS_CONFIG[s];
                const currentIdx = STATUSES.indexOf(filing.status);
                const isCompleted = idx <= currentIdx;
                const isCurrent = s === filing.status;
                return (
                  <div key={s} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${isCurrent ? 'bg-primary/10 font-medium' : ''}`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${isCompleted ? cfg.color : 'bg-border'}`} />
                    <span className={isCompleted ? cfg.textColor : 'text-muted-foreground'}>{cfg.label}</span>
                    {isCurrent && <span className="ml-auto text-primary text-[10px] font-semibold">CURRENT</span>}
                  </div>
                );
              })}
            </div>

            <h3 className="text-sm font-semibold text-foreground mb-2">Status History</h3>
            <StatusTimeline history={filing.status_history} />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-6 pt-4 border-t border-border">
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting} className="gap-1.5" data-testid="delete-filing-btn">
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-filing-btn">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
